import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {NucleotideType} from 'biojs/Util';
import {OLIGO_NUCLEOTIDE_TYPE_TO_POLYMER_SUBTYPE} from 'constants/MolbioConstants';
import {Section} from 'constants/NavConstants';
import {QueryingModPatternTemplateOligoMap} from 'modbio/bulk-edit-mods/BulkEditModsOligoMap';
import {MAX_NUM_TARGET_CHIPS} from 'modbio/bulk-edit-mods/ModPatternConstants';
import {PolymerSearchView} from 'molbio/BioentitySearchView';
import ModifiedNucleotidesLegend from 'molbio/monomers/ModifiedNucleotidesLegend';
import {FONT_PROXIMA_13, FONT_PROXIMA_14} from 'styles/FontConstants';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import ChipList from 'ui-library/chips/ChipList';
import {GRAY_DARKER} from 'ui-library/Colors';
import {RowSelectionMode} from 'ui-library/scrollable/ScrollableTableConstants';

export interface ChooseModPatternPaneProps {
  targetOligoIds: Array<string>;
  templateOligoId: string | null;
  onSetTemplateOligoId: (templateOligoId: string | null) => void;
  nucleotideType: NucleotideType;
}

export default function ChooseModPatternPane({
  targetOligoIds,
  templateOligoId,
  onSetTemplateOligoId,
  nucleotideType,
}: ChooseModPatternPaneProps): JSX.Element {
  const setSelectedTemplateId = (newSelectedIds: Array<string>): void => {
    if (newSelectedIds.length === 0) {
      onSetTemplateOligoId(null);
    } else {
      onSetTemplateOligoId(newSelectedIds[0]);
    }
  };
  const selectedIds = templateOligoId != null ? [templateOligoId] : [];
  return (
    <div className={css(styles.paneContainer)}>
      <div className={css(styles.targetList)}>
        <span className={css(styles.text)}>
          Select the oligo whose pattern of modifications you wish to apply to
        </span>
        <ChipList
          itemIds={targetOligoIds.slice(0, Math.min(MAX_NUM_TARGET_CHIPS, targetOligoIds.length))}
          maxChips={MAX_NUM_TARGET_CHIPS}
          chipSize={ChipSize.MINI}
          isVertical={false}
          showChipsAsLinks={false}
        />
        {targetOligoIds.length > MAX_NUM_TARGET_CHIPS && (
          <span className={css(styles.text)}>{`+ ${targetOligoIds.length - MAX_NUM_TARGET_CHIPS} more`}</span>
        )}
      </div>
      <div className={css(styles.sequenceSearchView)}>
        <PolymerSearchView
          selectedBioentityIds={selectedIds}
          setSelectedBioentityIds={setSelectedTemplateId}
          polymerSubtypes={[OLIGO_NUCLEOTIDE_TYPE_TO_POLYMER_SUBTYPE[nucleotideType]]}
          navSection={Section.MOD_PATTERNS}
          rowSelectionMode={RowSelectionMode.SINGLE}
          alwaysShowSelector={true}
          searchLimit={8}
        />
      </div>
      <div className={css(styles.previewPatternContainer)}>
        <div>
          <span className={css(styles.previewText)}>Preview pattern</span>
          {templateOligoId && <BenchChip id={templateOligoId} size={ChipSize.MINI} isLink={false} />}
        </div>
        {templateOligoId != null ? (
          <div>
            <div className={`${css(styles.templateMap)} scrollbar-override`}>
              <QueryingModPatternTemplateOligoMap oligoId={templateOligoId} />
            </div>
            <div className={css(styles.legend)}>
              <ModifiedNucleotidesLegend sequenceId={templateOligoId} useNetworkQuery={true} />
            </div>
          </div>
        ) : (
          <span className={css(styles.placeholderText)}>Select an oligo to preview its pattern</span>
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  templateMap: {
    overflow: 'auto',
  },
  text: {
    ...FONT_PROXIMA_13,
  },
  paneContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
  },
  targetList: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    paddingLeft: 16,
    paddingRight: 16,
  },
  sequenceSearchView: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    width: '95%',
    height: '100%',
    minHeight: 425,
  },
  legend: {
    marginLeft: -20,
    marginTop: -16,
    marginBottom: -16,
  },
  previewText: {
    ...FONT_PROXIMA_14,
    marginRight: 6,
  },
  previewPatternContainer: {
    marginTop: 8,
    marginLeft: 16,
    width: '95%',
    height: 150,
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  placeholderText: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
  },
});
